import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { peopleKeys } from "@/features/people/api";
import type { WsRole } from "@/features/session/types";
import { workspaceKeys, workspacesApi } from "./api";

export function useWorkspaces() {
  return useQuery({ queryKey: workspaceKeys.list(), queryFn: workspacesApi.list });
}

export function useMembers(wsid: string | null) {
  return useQuery({
    queryKey: workspaceKeys.members(wsid ?? ""),
    queryFn: () => workspacesApi.members(wsid ?? ""),
    enabled: wsid !== null,
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => workspacesApi.create(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workspaceKeys.list() }),
  });
}

export function useDeleteWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (wsid: string) => workspacesApi.remove(wsid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workspaceKeys.all }),
  });
}

/** Every member change: refetch this workspace's members, and people (adding by
 *  email may have created someone). */
function useMemberMutation<T>(wsid: string, fn: (vars: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.members(wsid) });
      void queryClient.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

export function useAddMember(wsid: string) {
  return useMemberMutation(wsid, ({ email, role }: { email: string; role: WsRole }) =>
    workspacesApi.addMember(wsid, email, role),
  );
}

export function useSetMemberRole(wsid: string) {
  return useMemberMutation(wsid, ({ sub, role }: { sub: string; role: WsRole }) =>
    workspacesApi.setRole(wsid, sub, role),
  );
}

export function useRemoveMember(wsid: string) {
  return useMemberMutation(wsid, (sub: string) => workspacesApi.removeMember(wsid, sub));
}
